import React, { useMemo, useState, useRef, useCallback, Suspense, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Paper,
  Typography,
  Collapse,
  IconButton,
  Button,
  Card,
  CardContent,
  Avatar,
  useTheme,
  alpha,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
} from '@mui/material';
import {
  FullscreenOutlined,
  FullscreenExitOutlined,
  ZoomIn,
  ZoomOut,
  Explore,
} from '@mui/icons-material';
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Scatter,
  ComposedChart,
  Area,
  Line,
  Legend,
} from 'recharts';
import { useMetricSelection } from '../hooks/useMetricSelection';
import { useChartZoom } from '../hooks/useChartZoom';
import {
  transformActivityDataToChartFormat,
  synchronizeChartDomains,
  sampleDataForPerformance,
} from '../../../utils/chartUtils';
import type {
  ActivityStreamResponse,
  ChartMetricConfig,
  StackedChartDataPoint,
  MetricType,
} from '../../../types';
import type { WorkoutSegmentDetailResponse } from '../../../store/api/models/workoutModels';
// Lazy load ImmersiveAnalysisDialog to avoid circular dependency
const ImmersiveAnalysisDialog = React.lazy(() => import('./immersive-analysis/ImmersiveAnalysisDialog').then(module => ({ default: module.ImmersiveAnalysisDialog })));

// Debug flag - set to true only when debugging performance issues
const DEBUG_CHART = true; //process.env.NODE_ENV === 'development' && false;

// OPTIMIZATION: Define metric pairs that should be grouped together
// First metric = filled area, Second metric = line only
const METRIC_PAIRS: Record<MetricType, MetricType | null> = {
  'pace': 'gradeAdjustedPace',
  'gradeAdjustedPace': null, // Secondary, grouped with pace
  'heartRate': 'cadence',
  'cadence': null, // Secondary, grouped with heartRate
  'power': null,
  'speed': null,
  'altitude': null,
};

// Get the primary metric for a given metric (returns itself if primary, or the primary if secondary)
const getPrimaryMetric = (metricId: MetricType): MetricType => {
  for (const [primary, secondary] of Object.entries(METRIC_PAIRS)) {
    if (secondary === metricId) {
      return primary as MetricType;
    }
  }
  return metricId;
};

// Check if a metric is a secondary (should be grouped with another)
const isSecondaryMetric = (metricId: MetricType): boolean => {
  return Object.values(METRIC_PAIRS).includes(metricId);
};

interface WorkoutAwareChartProps {
  /** Activity stream data */
  streams: ActivityStreamResponse | null;
  /** Whether the data is loading */
  isLoading?: boolean;
  /** X-axis type: time or distance */
  xAxisType?: 'time' | 'distance';
  /** Initial collapsed state */
  initialCollapsed?: boolean;
  /** Chart height in normal mode */
  chartHeight?: number;
  /** Chart height in fullscreen mode */
  fullscreenChartHeight?: number;
  /** Workout segments with target zones */
  workoutSegments?: WorkoutSegmentDetailResponse[];
  /** Sport type for proper pace/speed handling */
  sportType?: string;
  /** Activity name for immersive analysis dialog */
  activityName?: string;
  /** Polyline for GPS fallback in immersive analysis */
  polyline?: string;
  /** BIDIRECTIONAL SYNC: External synced X value (from map hover) */
  externalSyncedXValue?: number | null;
  /** BIDIRECTIONAL SYNC: Callback when chart is hovered (updates external state like map) */
  onExternalHover?: (xValue: number | null, yValue: number | null, mouseX?: number, mouseY?: number) => void;
  /** Controls if the header and metric selectors are hidden */
  hideControls?: boolean;
  /** Externally controlled metrics */
  controlledMetrics?: MetricType[];
  /** Control visibility of immersive button */
  showImmersiveButton?: boolean;
  /** Lactate threshold heart rate for reference line */
  lactateThresholdHr?: number | null;
}

/**
 * Extended data point with workout target values and secondary metric values
 */
interface WorkoutChartDataPoint extends StackedChartDataPoint {
  // Heart rate targets
  targetHrMin?: number | null;
  targetHrMax?: number | null;
  targetHrBand?: number | null;
  // Pace targets
  targetPaceMin?: number | null;
  targetPaceMax?: number | null;
  targetPaceBand?: number | null;
  // Power targets
  targetPowerMin?: number | null;
  targetPowerMax?: number | null;
  targetPowerBand?: number | null;
  // Speed targets
  targetSpeedMin?: number | null;
  targetSpeedMax?: number | null;
  targetSpeedBand?: number | null;
  // Secondary metric value (for paired charts)
  secondaryValue?: number | null;
}

/**
 * Grouped metric chart props - supports primary + optional secondary metric
 */
interface GroupedMetricChartProps {
  data: WorkoutChartDataPoint[];
  primaryConfig: ChartMetricConfig;
  secondaryConfig?: ChartMetricConfig | null;
  syncedXValue: number | null;
  onHover: (xValue: number | null, yValue: number | null, mouseX?: number, mouseY?: number) => void;
  xAxisType: 'time' | 'distance';
  height: number;
  sharedXDomain: [number, number];
  isLoading: boolean;
  // Zoom props
  onClick?: (e: any) => void;
  onMouseMove?: (e: any) => void;
  isZoomModeActive?: boolean;
  sliderLeft?: number | null;
  sliderRight?: number | null;
  averageValue?: number | null;
  secondaryAverageValue?: number | null;
  effectiveYDomain?: [number, number];
  lactateThresholdHr?: number | null;
}

// OPTIMIZATION 3: Binary Search - Find closest point in O(log n) instead of O(n)
const findClosestPointBinarySearch = (data: any[], xValue: number, xKey: 'time' | 'distance'): any => {
  if (data.length === 0) return null;

  let left = 0;
  let right = data.length - 1;

  // Binary search to find the closest point
  while (left < right) {
    const mid = Math.floor((left + right) / 2);
    if (data[mid][xKey] < xValue) {
      left = mid + 1;
    } else {
      right = mid;
    }
  }

  // Check if the previous point is closer
  if (left > 0) {
    const distCurrent = Math.abs(data[left][xKey] - xValue);
    const distPrevious = Math.abs(data[left - 1][xKey] - xValue);
    return distPrevious < distCurrent ? data[left - 1] : data[left];
  }

  return data[left];
};

// OPTIMIZATION: Grouped Metric Chart component - handles 1 or 2 metrics in same chart
const GroupedMetricChartInner: React.FC<GroupedMetricChartProps> = ({
  data,
  primaryConfig,
  secondaryConfig,
  syncedXValue,
  onHover,
  xAxisType,
  height,
  sharedXDomain,
  isLoading,
  onClick,
  onMouseMove,
  isZoomModeActive = false,
  sliderLeft,
  sliderRight,
  averageValue,
  secondaryAverageValue,
  effectiveYDomain,
  lactateThresholdHr,
}) => {
  const theme = useTheme();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  // OPTIMIZATION 2 & 4: RAF throttling and cached rect
  const rafIdRef = useRef<number | null>(null);
  const cachedRectRef = useRef<DOMRect | null>(null);

  // Sample data for performance
  const sampledData = useMemo(() => {
    // If no data, return empty array early
    if (!data || !Array.isArray(data)) return [];

    const sampled = sampleDataForPerformance(data, 1000);

    // Debug logging - GATED behind DEBUG_CHART flag
    if (DEBUG_CHART) {
      const originalWithTargets = data.filter((d: any) =>
        d.targetHrMin || d.targetHrMax || d.targetPaceMin || d.targetPaceMax || d.targetPowerMin || d.targetPowerMax
      ).length;
      const sampledWithTargets = sampled.filter((d: any) =>
        d.targetHrMin || d.targetHrMax || d.targetPaceMin || d.targetPaceMax || d.targetPowerMin || d.targetPowerMax
      ).length;

      if (originalWithTargets > 0) {
        console.log(`🎲 Sampling [${primaryConfig.label}]:`, {
          originalPoints: data.length,
          originalWithTargets,
          sampledPoints: sampled.length,
          sampledWithTargets,
          preserved: `${((sampledWithTargets / originalWithTargets) * 100).toFixed(1)}%`,
        });
      }
    }

    return sampled;
  }, [data, primaryConfig.label]);

  // Calculate Y-axis domain and scale exponent - now considers both primary and secondary metrics
  const { yDomain, scaleExponent, transformedData, paceTransformConfig } = useMemo(() => {
    // Collect values from both primary and secondary metrics
    const primaryValues = sampledData.map(d => d.value).filter((v) => isFinite(v));
    const secondaryValues = secondaryConfig
      ? sampledData.map(d => d.secondaryValue).filter((v): v is number => v !== null && v !== undefined && isFinite(v))
      : [];

    const allValues = [...primaryValues, ...secondaryValues];

    if (allValues.length === 0) return { yDomain: [0, 100], scaleExponent: 1, transformedData: sampledData, paceTransformConfig: null };

    const min = Math.min(...allValues);
    const max = Math.max(...allValues);

    // Calculate quartiles using primary values (for scale calculation)
    const sortedValues = [...primaryValues].sort((a, b) => a - b);
    const q1Index = Math.floor(sortedValues.length * 0.25);
    const q3Index = Math.floor(sortedValues.length * 0.75);
    const q1 = sortedValues[q1Index] || min;
    const q3 = sortedValues[q3Index] || max;

    let domainMin = 0;
    let domainMax = max;
    let exponent = 1;
    let transformedData = sampledData;
    let paceTransformConfig = null;

    if (primaryConfig.invertAxis) {
      // For Pace (Inverted) - use combined min/max for domain
      const slowPaceCutoff = Math.min(max, Math.max(q3 * 1.3, min * 1.5));
      const fastestPace = Math.max(0.1, min - (min * 0.05));

      const transformPaceValue = (pace: number) => {
        if (pace <= q3) {
          const logMin = Math.log(Math.max(0.01, fastestPace));
          const logQ3 = Math.log(q3);
          const logPace = Math.log(Math.max(0.01, pace));
          return fastestPace + ((logPace - logMin) / (logQ3 - logMin)) * 0.7 * (slowPaceCutoff - fastestPace);
        } else {
          const baseValue = fastestPace + 0.7 * (slowPaceCutoff - fastestPace);
          return baseValue + ((pace - q3) / (slowPaceCutoff - q3)) * 0.3 * (slowPaceCutoff - fastestPace);
        }
      };

      transformedData = sampledData.map((d, idx) => {
        const result = {
          ...d,
          value: transformPaceValue(Math.min(d.value, slowPaceCutoff)),
          // Transform secondary value too (for adjusted pace)
          secondaryValue: d.secondaryValue !== null && d.secondaryValue !== undefined
            ? transformPaceValue(Math.min(d.secondaryValue, slowPaceCutoff))
            : null,
          targetPaceMin: d.targetPaceMin ? transformPaceValue(Math.min(d.targetPaceMin, slowPaceCutoff)) : null,
          targetPaceMax: d.targetPaceMax ? transformPaceValue(Math.min(d.targetPaceMax, slowPaceCutoff)) : null,
        };

        // Debug logging - GATED
        if (DEBUG_CHART && idx === 0 && (d.targetPaceMin || d.targetPaceMax)) {
          console.log('🔄 Pace transformation sample:', {
            original: { value: d.value, secondaryValue: d.secondaryValue, targetMin: d.targetPaceMin, targetMax: d.targetPaceMax },
            transformed: { value: result.value, secondaryValue: result.secondaryValue, targetMin: result.targetPaceMin, targetMax: result.targetPaceMax },
            domain: [domainMin, domainMax],
          });
        }

        return result;
      });

      domainMin = fastestPace;
      domainMax = slowPaceCutoff;

      paceTransformConfig = {
        min: fastestPace,
        max: slowPaceCutoff,
        q3: q3,
      };

      exponent = 1;
    } else {
      // For HR/Power/Speed/Cadence (Normal) - expand domain to fit both metrics
      // MODIFICATION: Don't start from zero, exclude zeros from the domain
      const nonZeroValues = allValues.filter(v => v > 0);

      if (nonZeroValues.length === 0) {
        // All values are zero or null, return a default domain
        return { yDomain: [0, 100], scaleExponent: 1, transformedData: sampledData, paceTransformConfig: null };
      }

      const nonZeroMin = Math.min(...nonZeroValues);
      const nonZeroMax = Math.max(...nonZeroValues);

      // Calculate a reasonable minimum that excludes zeros but still shows all data
      // For heart rate, we want to show a minimum of around 50 bpm if data exists
      // For cadence, we want to show a minimum of around 50 rpm if data exists
      // For power, we want to show a minimum of around 0 if data exists
      // For speed, we want to show a minimum of around 0 if data exists

      let minThreshold = 0;
      if (primaryConfig.id === 'heartRate') {
        minThreshold = Math.max(50, nonZeroMin * 0.8);
      } else if (primaryConfig.id === 'cadence') {
        minThreshold = Math.max(50, nonZeroMin * 0.8);
      } else if (primaryConfig.id === 'power') {
        minThreshold = Math.max(0, nonZeroMin * 0.8);
      } else if (primaryConfig.id === 'speed') {
        minThreshold = Math.max(0, nonZeroMin * 0.8);
      } else {
        minThreshold = Math.max(nonZeroMin * 0.8, 0);
      }

      domainMin = minThreshold;
      domainMax = 1.05 * nonZeroMax;

      const range = domainMax - domainMin;
      if (range > 0) {
        const pivotRatio = (q1 - domainMin) / range;
        const safePivot = Math.max(0.01, Math.min(0.99, pivotRatio));
        exponent = Math.log(0.25) / Math.log(safePivot);
      }
    }

    exponent = Math.max(0.1, Math.min(10, exponent));

    return { yDomain: [domainMin, domainMax], scaleExponent: exponent, transformedData, paceTransformConfig };
  }, [sampledData, primaryConfig.invertAxis, primaryConfig.id, secondaryConfig]);

  // Use effective Y domain if zoomed, otherwise use calculated domain
  const finalYDomain = effectiveYDomain || yDomain;

  // OPTIMIZATION 4: Cache getBoundingClientRect on mount and resize
  useEffect(() => {
    const updateCachedRect = () => {
      if (chartContainerRef.current) {
        cachedRectRef.current = chartContainerRef.current.getBoundingClientRect();
      }
    };

    updateCachedRect();
    window.addEventListener('resize', updateCachedRect);
    return () => window.removeEventListener('resize', updateCachedRect);
  }, []);

  // OPTIMIZATION: Stable callback for mouse move using useCallback
  const handleContainerMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!chartContainerRef.current || transformedData.length === 0) return;

    // OPTIMIZATION 2: RAF Throttling - Limit updates to browser refresh rate
    if (rafIdRef.current !== null) {
      return; // Already have a pending RAF
    }

    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = null;

      // OPTIMIZATION 4: Use cached rect instead of getBoundingClientRect
      const rect = cachedRectRef.current || chartContainerRef.current!.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;

      const chartMarginLeft = 55;
      const chartMarginRight = 10;
      const chartWidth = rect.width - chartMarginLeft - chartMarginRight;

      if (mouseX < chartMarginLeft || mouseX > rect.width - chartMarginRight) {
        return;
      }

      const relativeX = (mouseX - chartMarginLeft) / chartWidth;
      const [domainMin, domainMax] = sharedXDomain;
      const xValue = domainMin + relativeX * (domainMax - domainMin);

      // OPTIMIZATION 3: Binary search for closest data point
      const xKey = xAxisType === 'time' ? 'time' : 'distance';
      const dataPoint = findClosestPointBinarySearch(transformedData, xValue, xKey);

      if (dataPoint) {
        onHover(dataPoint[xKey], dataPoint.value, e.clientX, e.clientY);
      }
    });
  }, [transformedData, sharedXDomain, xAxisType, onHover]);

  const handleContainerMouseLeave = useCallback(() => {
    // Cancel any pending RAF
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    onHover(null, null);
  }, [onHover]);

  // Find the closest data point to the synced x value
  const closestDataPoint = useMemo(() => {
    if (syncedXValue === null || transformedData.length === 0) {
      return null;
    }

    // OPTIMIZATION 3: Use binary search for finding closest point
    const xKey = xAxisType === 'time' ? 'time' : 'distance';
    return findClosestPointBinarySearch(transformedData, syncedXValue, xKey);
  }, [syncedXValue, transformedData, xAxisType]);

  // Determine target line data keys based on metric type (primary metric only)
  const targetMinKey = primaryConfig.id === 'heartRate' ? 'targetHrMin'
    : primaryConfig.id === 'pace' ? 'targetPaceMin'
      : primaryConfig.id === 'power' ? 'targetPowerMin'
        : primaryConfig.id === 'speed' ? 'targetSpeedMin'
          : null;

  const targetMaxKey = primaryConfig.id === 'heartRate' ? 'targetHrMax'
    : primaryConfig.id === 'pace' ? 'targetPaceMax'
      : primaryConfig.id === 'power' ? 'targetPowerMax'
        : primaryConfig.id === 'speed' ? 'targetSpeedMax'
          : null;

  // Debug logging - GATED behind DEBUG_CHART flag
  useEffect(() => {
    if (!DEBUG_CHART) return;

    if (transformedData.length > 0) {
      const pointsWithTargets = transformedData.filter((d: any) => {
        return (targetMinKey && d[targetMinKey] !== null && d[targetMinKey] !== undefined) ||
          (targetMaxKey && d[targetMaxKey] !== null && d[targetMaxKey] !== undefined);
      });

      if (pointsWithTargets.length > 0) {
        console.log(`📈 GroupedMetricChart [${primaryConfig.label}${secondaryConfig ? ` + ${secondaryConfig.label}` : ''}]:`, {
          totalPoints: transformedData.length,
          pointsWithTargets: pointsWithTargets.length,
          hasSecondary: !!secondaryConfig,
        });
      }
    }
  }, [transformedData, primaryConfig.label, secondaryConfig, targetMinKey, targetMaxKey]);

  // Memoize formatXAxis to avoid recreating on every render
  const formatXAxis = useCallback((value: number) => {
    if (xAxisType === 'time') {
      const minutes = Math.floor(value / 60);
      const seconds = Math.floor(value % 60);
      return `${minutes}:${String(seconds).padStart(2, '0')}`;
    } else {
      return `${(value / 1000).toFixed(1)}km`;
    }
  }, [xAxisType]);

  if (isLoading) {
    return (
      <Card
        elevation={0}
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Loading...
        </Typography>
      </Card>
    );
  }

  if (sampledData.length === 0) {
    return (
      <Card
        elevation={0}
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No data available for {primaryConfig.label}
        </Typography>
      </Card>
    );
  }

  // Reverse transformation function for Y-axis tick labels (pace only)
  const reverseTransformPace = (transformedValue: number) => {
    if (!paceTransformConfig) return transformedValue;

    const { min, max, q3 } = paceTransformConfig;
    const cutoffPoint = min + 0.7 * (max - min);

    if (transformedValue <= cutoffPoint) {
      const logMin = Math.log(Math.max(0.01, min));
      const logQ3 = Math.log(q3);
      const ratio = (transformedValue - min) / (cutoffPoint - min);
      return Math.exp(logMin + ratio * (logQ3 - logMin));
    } else {
      const ratio = (transformedValue - cutoffPoint) / (max - cutoffPoint);
      return q3 + ratio * (max - q3);
    }
  };

  // Memoized check for target data existence
  const hasHrTargetData = targetMinKey === 'targetHrMin' && targetMaxKey === 'targetHrMax' &&
    transformedData.some((d: any) =>
      d.targetHrMin !== null && d.targetHrMin !== undefined &&
      d.targetHrMax !== null && d.targetHrMax !== undefined
    );

  const hasPaceTargetData = targetMinKey === 'targetPaceMin' && targetMaxKey === 'targetPaceMax' &&
    transformedData.some((d: any) =>
      d.targetPaceMin !== null && d.targetPaceMin !== undefined &&
      d.targetPaceMax !== null && d.targetPaceMax !== undefined
    );

  const hasPowerTargetData = targetMinKey === 'targetPowerMin' && targetMaxKey === 'targetPowerMax' &&
    transformedData.some((d: any) =>
      d.targetPowerMin !== null && d.targetPowerMin !== undefined &&
      d.targetPowerMax !== null && d.targetPowerMax !== undefined
    );

  const hasSpeedTargetData = targetMinKey === 'targetSpeedMin' && targetMaxKey === 'targetSpeedMax' &&
    transformedData.some((d: any) =>
      d.targetSpeedMin !== null && d.targetSpeedMin !== undefined &&
      d.targetSpeedMax !== null && d.targetSpeedMax !== undefined
    );

  const hasTargetMinData = targetMinKey && transformedData.some((d: any) => d[targetMinKey] !== null && d[targetMinKey] !== undefined);
  const hasTargetMaxData = targetMaxKey && transformedData.some((d: any) => d[targetMaxKey] !== null && d[targetMaxKey] !== undefined);

  // Check if secondary data exists
  const hasSecondaryData = secondaryConfig && transformedData.some((d: any) =>
    d.secondaryValue !== null && d.secondaryValue !== undefined
  );

  // Grey color for target lines
  const targetLineColor = theme.palette.mode === 'dark' ? '#888888' : '#6b7280';
  const targetFillColor = theme.palette.mode === 'dark' ? '#666666' : '#9ca3af';

  // Generate chart title
  const chartTitle = secondaryConfig
    ? `${primaryConfig.label} / ${secondaryConfig.label}`
    : primaryConfig.label;

  return (
    <Card
      elevation={0}
      sx={{
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        borderRadius: 0,
        bgcolor: 'transparent',
        transition: 'all 0.3s ease',
        overflow: 'visible',
        borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <CardContent sx={{ p: 1, position: 'relative', overflow: 'visible', height: '100%', '&:last-child': { pb: 1 } }}>
        {/* Chart header with both metric indicators */}
        <Box sx={{ mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: 40 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">
              {chartTitle}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* Primary metric indicator */}
            <Avatar
              sx={{
                bgcolor: alpha(primaryConfig.color, 0.15),
                color: primaryConfig.color,
                width: 28,
                height: 28
              }}
              title={primaryConfig.label}
            >
              {primaryConfig.icon && <primaryConfig.icon sx={{ fontSize: 16 }} />}
            </Avatar>
            {/* Secondary metric indicator */}
            {secondaryConfig && (
              <Avatar
                sx={{
                  bgcolor: alpha(secondaryConfig.color, 0.15),
                  color: secondaryConfig.color,
                  width: 28,
                  height: 28,
                  border: `2px dashed ${secondaryConfig.color}`,
                }}
                title={secondaryConfig.label}
              >
                {secondaryConfig.icon && <secondaryConfig.icon sx={{ fontSize: 14 }} />}
              </Avatar>
            )}
          </Box>
        </Box>

        {/* Chart */}
        <Box
          ref={chartContainerRef}
          onMouseMove={handleContainerMouseMove}
          onMouseLeave={handleContainerMouseLeave}
          sx={{ width: '100%', height: `calc(100% - 56px)`, cursor: 'crosshair' }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={transformedData}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              syncId="activityCharts"
              syncMethod="value"
              onClick={onClick}
              onMouseMove={onMouseMove}
            >
              <defs>
                <linearGradient id={`gradient-${primaryConfig.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={primaryConfig.color} stopOpacity={0.6} />
                  <stop offset="95%" stopColor={primaryConfig.color} stopOpacity={0.1} />
                </linearGradient>
              </defs>

              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.divider, 0.3)}
                vertical={false}
              />

              <XAxis
                dataKey={xAxisType === 'time' ? 'time' : 'distance'}
                domain={sharedXDomain}
                type="number"
                allowDataOverflow={false}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                stroke={theme.palette.divider}
                tickFormatter={formatXAxis}
              />

              <YAxis
                domain={finalYDomain}
                scale={primaryConfig.invertAxis ? 'linear' : 'auto'}
                type="number"
                allowDataOverflow={true}
                reversed={primaryConfig.invertAxis}
                tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
                stroke={theme.palette.divider}
                tickFormatter={(value) => {
                  const actualValue = primaryConfig.invertAxis ? reverseTransformPace(value) : value;

                  if (primaryConfig.formatValue) {
                    return primaryConfig.formatValue(actualValue);
                  }
                  return actualValue.toFixed(0);
                }}
                width={55}
                label={{
                  value: primaryConfig.unit,
                  angle: -90,
                  position: 'insideLeft',
                  fill: theme.palette.text.secondary,
                  fontSize: 10,
                  style: { textAnchor: 'middle' },
                  offset: 10,
                }}
              />

              {/* Invisible tooltip to enable continuous mouse tracking */}
              <Tooltip
                content={() => null}
                cursor={{
                  stroke: 'transparent',
                  strokeWidth: 0,
                }}
                isAnimationActive={false}
                allowEscapeViewBox={{ x: false, y: true }}
              />

              {/* Target Zone Filled Areas - HR */}
              {hasHrTargetData && (
                <>
                  <Area
                    type="stepAfter"
                    dataKey="targetHrMin"
                    stackId="hrTarget"
                    stroke="none"
                    fill="transparent"
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="targetHrBand"
                    stackId="hrTarget"
                    stroke="none"
                    fill={targetFillColor}
                    fillOpacity={0.2}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Target Zone Filled Areas - Pace */}
              {hasPaceTargetData && (
                <>
                  <Area
                    type="stepAfter"
                    dataKey="targetPaceMin"
                    stackId="paceTarget"
                    stroke="none"
                    fill="transparent"
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="targetPaceBand"
                    stackId="paceTarget"
                    stroke="none"
                    fill={targetFillColor}
                    fillOpacity={0.2}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Target Zone Filled Areas - Power */}
              {hasPowerTargetData && (
                <>
                  <Area
                    type="stepAfter"
                    dataKey="targetPowerMin"
                    stackId="powerTarget"
                    stroke="none"
                    fill="transparent"
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="targetPowerBand"
                    stackId="powerTarget"
                    stroke="none"
                    fill={targetFillColor}
                    fillOpacity={0.2}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Target Zone Filled Areas - Speed */}
              {hasSpeedTargetData && (
                <>
                  <Area
                    type="stepAfter"
                    dataKey="targetSpeedMin"
                    stackId="speedTarget"
                    stroke="none"
                    fill="transparent"
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                  <Area
                    type="stepAfter"
                    dataKey="targetSpeedBand"
                    stackId="speedTarget"
                    stroke="none"
                    fill={targetFillColor}
                    fillOpacity={0.2}
                    connectNulls={false}
                    isAnimationActive={false}
                  />
                </>
              )}

              {/* Primary metric - filled area chart */}
              <Area
                type="monotone"
                dataKey="value"
                stroke={primaryConfig.color}
                strokeWidth={2}
                fill={`url(#gradient-${primaryConfig.id})`}
                baseValue={primaryConfig.invertAxis ? finalYDomain[1] : finalYDomain[0]}
                isAnimationActive={false}
                connectNulls
                activeDot={false}
                name={primaryConfig.label}
              />

              {/* Secondary metric - line only (no fill) */}
              {hasSecondaryData && secondaryConfig && (
                <Line
                  type="monotone"
                  dataKey="secondaryValue"
                  stroke={secondaryConfig.color}
                  strokeWidth={2}
                  strokeDasharray="4 2"
                  dot={false}
                  connectNulls
                  isAnimationActive={false}
                  name={secondaryConfig.label}
                />
              )}

              {/* Target Min Line - horizontal grey dashed line */}
              {hasTargetMinData && (
                <Line
                  type="stepAfter"
                  dataKey={targetMinKey!}
                  stroke={targetLineColor}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                  name="Target Min"
                />
              )}

              {/* Target Max Line - horizontal grey dashed line */}
              {hasTargetMaxData && (
                <Line
                  type="stepAfter"
                  dataKey={targetMaxKey!}
                  stroke={targetLineColor}
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                  connectNulls={false}
                  isAnimationActive={false}
                  name="Target Max"
                />
              )}

              {/* Lactate Threshold HR Reference Line - only for heart rate chart */}
              {primaryConfig.id === 'heartRate' && lactateThresholdHr !== null && lactateThresholdHr !== undefined && (
                <ReferenceLine
                  y={lactateThresholdHr}
                  stroke={theme.palette.mode === 'dark' ? '#ef5350' : '#e53935'}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  label={{
                    value: 'LT2 HR',
                    position: 'insideTopRight',
                    fill: theme.palette.mode === 'dark' ? '#ef5350' : '#e53935',
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                  ifOverflow="extend"
                />
              )}

              {/* Synchronized reference line - render last to be on top of everything */}
              {syncedXValue !== null && (
                <ReferenceLine
                  x={syncedXValue}
                  stroke={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main}
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  opacity={0.8}
                />
              )}

              {/* Highlight dot at the synced position - primary metric */}
              {closestDataPoint && syncedXValue !== null && (
                <Scatter
                  data={[closestDataPoint]}
                  dataKey="value"
                  fill={primaryConfig.color}
                  shape={(props: any) => {
                    const { cx, cy } = props;
                    if (!cx || !cy || !isFinite(cx) || !isFinite(cy)) return <></>;
                    return (
                      <circle
                        cx={cx}
                        cy={cy}
                        r={6}
                        fill={primaryConfig.color}
                        stroke={theme.palette.background.paper}
                        strokeWidth={3}
                        style={{
                          filter: `drop-shadow(0px 2px 4px ${alpha(primaryConfig.color, 0.5)})`,
                        }}
                      />
                    );
                  }}
                  isAnimationActive={false}
                  yAxisId={0}
                  xAxisId={0}
                />
              )}

              {/* Highlight dot at the synced position - secondary metric */}
              {closestDataPoint && syncedXValue !== null && hasSecondaryData && secondaryConfig &&
                closestDataPoint.secondaryValue !== null && closestDataPoint.secondaryValue !== undefined && (
                  <Scatter
                    data={[{ ...closestDataPoint, value: closestDataPoint.secondaryValue }]}
                    dataKey="value"
                    fill={secondaryConfig.color}
                    shape={(props: any) => {
                      const { cx, cy } = props;
                      if (!cx || !cy || !isFinite(cx) || !isFinite(cy)) return <></>;
                      return (
                        <circle
                          cx={cx}
                          cy={cy}
                          r={5}
                          fill={secondaryConfig.color}
                          stroke={theme.palette.background.paper}
                          strokeWidth={2}
                          strokeDasharray="2 2"
                          style={{
                            filter: `drop-shadow(0px 2px 4px ${alpha(secondaryConfig.color, 0.5)})`,
                          }}
                        />
                      );
                    }}
                    isAnimationActive={false}
                    yAxisId={0}
                    xAxisId={0}
                  />
                )}

              {/* Slider boundary lines for zoom selection */}
              {isZoomModeActive && sliderLeft !== null && sliderLeft !== undefined && (
                <ReferenceLine
                  x={sliderLeft}
                  stroke={theme.palette.primary.main}
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  label={{
                    value: 'Start',
                    position: 'top',
                    fill: theme.palette.primary.main,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}
              {isZoomModeActive && sliderRight !== null && sliderRight !== undefined && (
                <ReferenceLine
                  x={sliderRight}
                  stroke={theme.palette.secondary.main}
                  strokeWidth={3}
                  strokeDasharray="5 5"
                  label={{
                    value: 'End',
                    position: 'top',
                    fill: theme.palette.secondary.main,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}

              {/* Shaded area between sliders */}
              {isZoomModeActive && sliderLeft !== null && sliderRight !== null && (
                <ReferenceArea
                  x1={sliderLeft}
                  x2={sliderRight}
                  strokeOpacity={0.3}
                  fill={theme.palette.primary.main}
                  fillOpacity={0.05}
                />
              )}

              {/* Average line for zoomed selection - primary */}
              {averageValue != null && (
                <ReferenceLine
                  y={averageValue}
                  stroke={primaryConfig.color}
                  strokeWidth={2}
                  strokeDasharray="3 3"
                  label={{
                    value: `Avg: ${primaryConfig.formatValue ? primaryConfig.formatValue(averageValue) : averageValue.toFixed(1)}`,
                    position: 'insideTopRight',
                    fill: primaryConfig.color,
                    fontSize: 11,
                    fontWeight: 600,
                  }}
                />
              )}

              {/* Average line for zoomed selection - secondary */}
              {secondaryAverageValue != null && secondaryConfig && (
                <ReferenceLine
                  y={secondaryAverageValue}
                  stroke={secondaryConfig.color}
                  strokeWidth={2}
                  strokeDasharray="6 3"
                  label={{
                    value: `Avg: ${secondaryConfig.formatValue ? secondaryConfig.formatValue(secondaryAverageValue) : secondaryAverageValue.toFixed(1)}`,
                    position: 'insideBottomRight',
                    fill: secondaryConfig.color,
                    fontSize: 10,
                    fontWeight: 600,
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};

// OPTIMIZATION: Wrap GroupedMetricChart in React.memo with custom comparator
const GroupedMetricChart = React.memo(GroupedMetricChartInner, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.primaryConfig === nextProps.primaryConfig &&
    prevProps.secondaryConfig === nextProps.secondaryConfig &&
    prevProps.syncedXValue === nextProps.syncedXValue &&
    prevProps.xAxisType === nextProps.xAxisType &&
    prevProps.height === nextProps.height &&
    prevProps.sharedXDomain[0] === nextProps.sharedXDomain[0] &&
    prevProps.sharedXDomain[1] === nextProps.sharedXDomain[1] &&
    prevProps.isLoading === nextProps.isLoading &&
    prevProps.isZoomModeActive === nextProps.isZoomModeActive &&
    prevProps.sliderLeft === nextProps.sliderLeft &&
    prevProps.sliderRight === nextProps.sliderRight &&
    prevProps.averageValue === nextProps.averageValue &&
    prevProps.secondaryAverageValue === nextProps.secondaryAverageValue &&
    prevProps.effectiveYDomain === nextProps.effectiveYDomain
  );
});

/**
 * Metric selector chip component
 */
interface MetricSelectorChipProps {
  config: ChartMetricConfig;
  selected: boolean;
  onToggle: (id: MetricType) => void;
  disabled?: boolean;
  isSecondary?: boolean;
}

const MetricSelectorChipInner: React.FC<MetricSelectorChipProps> = ({
  config,
  selected,
  onToggle,
  disabled = false,
  isSecondary = false,
}) => {
  const theme = useTheme();
  const Icon = config.icon;

  const handleClick = useCallback(() => {
    if (!disabled) {
      onToggle(config.id);
    }
  }, [disabled, onToggle, config.id]);

  return (
    <Card
      onClick={handleClick}
      elevation={selected ? 2 : 0}
      sx={{
        display: 'inline-flex',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        border: isSecondary ? '2px dashed' : '2px solid',
        borderColor: selected ? config.color : 'divider',
        bgcolor: selected ? alpha(config.color, 0.1) : 'background.paper',
        transition: 'all 0.2s ease',
        '&:hover': disabled ? {} : {
          borderColor: selected ? config.color : alpha(theme.palette.divider, 0.5),
          bgcolor: selected ? alpha(config.color, 0.15) : alpha(theme.palette.action.hover, 0.05),
          transform: 'translateY(-1px)',
        }
      }}
    >
      <CardContent sx={{
        p: '10px 16px !important',
        display: 'flex',
        alignItems: 'center',
        gap: 1.5
      }}>
        <Avatar
          sx={{
            bgcolor: selected ? alpha(config.color, 0.2) : alpha(theme.palette.action.active, 0.1),
            color: selected ? config.color : theme.palette.text.secondary,
            width: 24,
            height: 24,
            border: isSecondary ? `1px dashed ${selected ? config.color : theme.palette.divider}` : 'none',
          }}
        >
          {Icon && <Icon sx={{ fontSize: 14 }} />}
        </Avatar>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{ color: selected ? config.color : 'text.primary' }}
        >
          {config.label}
        </Typography>
      </CardContent>
    </Card>
  );
};

// OPTIMIZATION: Memoize MetricSelectorChip
const MetricSelectorChip = React.memo(MetricSelectorChipInner);

export interface ChartRef {
  handleZoomIn: () => void;
  handleResetZoom: () => void;
}

/**
 * WorkoutAwareChart - Displays activity streams with workout target lines
 * Now supports grouped metrics for better performance
 */
export const WorkoutAwareChart = React.forwardRef<ChartRef, WorkoutAwareChartProps>(({
  streams,
  isLoading = false,
  xAxisType = 'time',
  initialCollapsed = false,
  chartHeight = 200,
  fullscreenChartHeight = 300,
  workoutSegments,
  sportType = 'run',
  activityName = 'Activity',
  polyline,
  externalSyncedXValue,
  onExternalHover,
  controlledMetrics,
  hideControls,
  showImmersiveButton = true,
  lactateThresholdHr
}, ref) => {
  const theme = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const [currentXAxisType, setCurrentXAxisType] = useState<'time' | 'distance'>(xAxisType);

  // Sync internal X axis type if prop changes
  useEffect(() => {
    setCurrentXAxisType(xAxisType);
  }, [xAxisType]);

  // OPTIMIZATION 1: State Batching - Combine hover-related states into single object
  const [hoverState, setHoverState] = useState<{
    syncedXValue: number | null;
    syncedYValue: number | null;
    isHovering: boolean;
    cursorPosition: { x: number; y: number } | null;
  }>({
    syncedXValue: null,
    syncedYValue: null,
    isHovering: false,
    cursorPosition: null,
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isImmersiveAnalysisOpen, setIsImmersiveAnalysisOpen] = useState(false);

  // Use metric selection hook
  const {
    selectedMetrics: internalSelectedMetrics,
    availableMetrics,
    toggleMetric,
    canDeselectMetric,
    selectAll,
    resetToDefault,
  } = useMetricSelection(streams, ['pace'], sportType);

  // Use controlled metrics if provided, otherwise internal state
  const selectedMetrics = controlledMetrics || internalSelectedMetrics;

  // Get metric configs for selected metrics
  const selectedMetricConfigs = useMemo(() => {
    return availableMetrics.filter((m: { id: any; }) => selectedMetrics.includes(m.id));
  }, [availableMetrics, selectedMetrics]);

  const isRun = sportType?.toLowerCase().includes('run');

  // OPTIMIZATION: Memoize conversion functions
  const convertToMinPerKm = useCallback((mps: number | null): number | null => {
    if (mps === null || mps === undefined || mps === 0) return null;
    return mps / 60;
  }, []);

  const convertSecondsPerKmToMinPerKm = useCallback((mps: number | null): number | null => {
    if (mps === null || mps === undefined || mps === 0) return null;
    return mps / 60;
  }, []);

  const convertToKmh = useCallback((mps: number | null): number | null => {
    if (mps === null || mps === undefined) return null;
    return mps * 3.6;
  }, []);

  // OPTIMIZATION: Group metrics into primary/secondary pairs
  const groupedMetricConfigs = useMemo(() => {
    const groups: Array<{
      primary: ChartMetricConfig;
      secondary: ChartMetricConfig | null;
    }> = [];
    const processedSecondaries = new Set<MetricType>();

    selectedMetricConfigs.forEach((config: ChartMetricConfig) => {
      // Skip if this is a secondary that's already been grouped
      if (processedSecondaries.has(config.id)) {
        return;
      }

      // Check if this metric has a secondary
      const secondaryId = METRIC_PAIRS[config.id];

      if (secondaryId && selectedMetrics.includes(secondaryId)) {
        // This is a primary with an active secondary
        const secondaryConfig = availableMetrics.find((m: ChartMetricConfig) => m.id === secondaryId);
        groups.push({
          primary: config,
          secondary: secondaryConfig || null,
        });
        processedSecondaries.add(secondaryId);
      } else if (!isSecondaryMetric(config.id)) {
        // This is a standalone primary (no secondary selected)
        groups.push({
          primary: config,
          secondary: null,
        });
      }
      // If it's a secondary without its primary selected, it becomes a standalone
      else if (isSecondaryMetric(config.id)) {
        const primaryId = getPrimaryMetric(config.id);
        if (!selectedMetrics.includes(primaryId)) {
          groups.push({
            primary: config,
            secondary: null,
          });
        }
      }
    });

    return groups;
  }, [selectedMetricConfigs, selectedMetrics, availableMetrics]);

  // Transform data for each metric group with workout targets
  const chartDataSets = useMemo(() => {
    if (!streams) {
      return groupedMetricConfigs.map(() => []);
    }

    return groupedMetricConfigs.map(({ primary: config, secondary: secondaryConfig }) => {
      let baseData: WorkoutChartDataPoint[] = transformActivityDataToChartFormat(streams, config.id, sportType);

      // If we have a secondary metric, merge its data
      let secondaryData: WorkoutChartDataPoint[] = [];
      if (secondaryConfig) {
        secondaryData = transformActivityDataToChartFormat(streams, secondaryConfig.id, sportType);
      }

      // Merge secondary data into primary data points
      if (secondaryData.length > 0) {
        const secondaryMap = new Map<number, number>();
        secondaryData.forEach(point => {
          secondaryMap.set(Math.round(point.time), point.value);
        });

        baseData = baseData.map(point => ({
          ...point,
          secondaryValue: secondaryMap.get(Math.round(point.time)) ?? null,
        }));
      }

      // If workout segments are provided, merge target data
      if (workoutSegments && workoutSegments.length > 0) {
        const targetMap = new Map<number, any>();
        let cumulativeTime = 0;
        let cumulativeDistance = 0;

        workoutSegments.forEach((segment, segmentIndex) => {
          if (!segment.streams.time || segment.streams.time.length === 0) return;

          const firstTime = segment.streams.time[0];
          const firstDistance = segment.streams.distance?.[0] ?? 0;
          const timeOffset = segmentIndex === 0 ? 0 : cumulativeTime - firstTime;
          const distanceOffset = segmentIndex === 0 ? 0 : cumulativeDistance - firstDistance;

          segment.streams.time.forEach((time, i) => {
            const adjustedTime = time + timeOffset;
            const distance = segment.streams.distance?.[i];
            const adjustedDistance = (distance ?? 0) + distanceOffset;

            // Get target streams based on metric type
            let targetMin = null;
            let targetMax = null;

            if (config.id === 'heartRate') {
              targetMin = segment.workout_streams?.target_heartrate_min?.[i] ?? null;
              targetMax = segment.workout_streams?.target_heartrate_max?.[i] ?? null;
            } else if (config.id === 'pace') {
              if (isRun) {
                const paceMinMs = segment.workout_streams?.target_pace_min?.[i];
                const paceMaxMs = segment.workout_streams?.target_pace_max?.[i];
                targetMin = paceMinMs !== null && paceMinMs !== undefined ? convertSecondsPerKmToMinPerKm(paceMinMs) : null;
                targetMax = paceMaxMs !== null && paceMaxMs !== undefined ? convertSecondsPerKmToMinPerKm(paceMaxMs) : null;
              } else {
                const speedMin = segment.workout_streams?.target_speed_min?.[i];
                const speedMax = segment.workout_streams?.target_speed_max?.[i];
                targetMin = speedMin !== null && speedMin !== undefined ? convertToKmh(speedMin) : null;
                targetMax = speedMax !== null && speedMax !== undefined ? convertToKmh(speedMax) : null;
              }
            } else if (config.id === 'power') {
              targetMin = segment.workout_streams?.target_power_min?.[i] ?? null;
              targetMax = segment.workout_streams?.target_power_max?.[i] ?? null;
            } else if (config.id === 'speed') {
              const speedMin = segment.workout_streams?.target_speed_min?.[i];
              const speedMax = segment.workout_streams?.target_speed_max?.[i];
              targetMin = speedMin !== null && speedMin !== undefined ? convertToKmh(speedMin) : null;
              targetMax = speedMax !== null && speedMax !== undefined ? convertToKmh(speedMax) : null;
            }

            targetMap.set(Math.round(adjustedTime), {
              targetMin,
              targetMax,
            });
          });

          const lastTime = segment.streams.time[segment.streams.time.length - 1];
          const lastDistance = segment.streams.distance?.[segment.streams.distance.length - 1] ?? 0;
          cumulativeTime = lastTime + timeOffset;
          cumulativeDistance = lastDistance + distanceOffset;
        });

        baseData = baseData.map((point) => {
          const targetData = targetMap.get(Math.round(point.time));
          if (targetData) {
            if (config.id === 'heartRate') {
              const targetBand = (targetData.targetMin !== null && targetData.targetMax !== null)
                ? targetData.targetMax - targetData.targetMin
                : null;
              return {
                ...point,
                targetHrMin: targetData.targetMin,
                targetHrMax: targetData.targetMax,
                targetHrBand: targetBand,
              };
            } else if (config.id === 'pace') {
              const p1 = targetData.targetMin;
              const p2 = targetData.targetMax;
              const sortedPaceMin = (p1 !== null && p2 !== null) ? Math.min(p1, p2) : (p1 ?? p2);
              const sortedPaceMax = (p1 !== null && p2 !== null) ? Math.max(p1, p2) : (p2 ?? p1);
              const targetBand = (sortedPaceMin !== null && sortedPaceMax !== null)
                ? sortedPaceMax - sortedPaceMin
                : null;
              return {
                ...point,
                targetPaceMin: sortedPaceMin,
                targetPaceMax: sortedPaceMax,
                targetPaceBand: targetBand,
              };
            } else if (config.id === 'power') {
              const targetBand = (targetData.targetMin !== null && targetData.targetMax !== null)
                ? targetData.targetMax - targetData.targetMin
                : null;
              return {
                ...point,
                targetPowerMin: targetData.targetMin,
                targetPowerMax: targetData.targetMax,
                targetPowerBand: targetBand,
              };
            } else if (config.id === 'speed') {
              const targetBand = (targetData.targetMin !== null && targetData.targetMax !== null)
                ? targetData.targetMax - targetData.targetMin
                : null;
              return {
                ...point,
                targetSpeedMin: targetData.targetMin,
                targetSpeedMax: targetData.targetMax,
                targetSpeedBand: targetBand,
              };
            }
          }
          return point;
        });
      }

      return baseData;
    });
  }, [streams, groupedMetricConfigs, workoutSegments, isRun, convertToKmh, convertSecondsPerKmToMinPerKm, sportType]);

  // Calculate shared X-axis domain
  const sharedXDomain = useMemo(() => {
    return synchronizeChartDomains(chartDataSets, currentXAxisType);
  }, [chartDataSets, currentXAxisType]);

  // Use zoom hook - pass selectedMetricConfigs for backward compatibility
  const {
    isZoomed,
    isZoomModeActive,
    sliderLeft,
    sliderRight,
    effectiveXDomain,
    getEffectiveYDomain,
    getMetricAverage,
    toggleZoomMode,
    handleChartClick,
    handleMouseMove: handleZoomMouseMove,
    resetZoom,
  } = useChartZoom({
    chartDataSets,
    selectedMetricConfigs,
    xAxisType: currentXAxisType,
    originalXDomain: sharedXDomain,
  });

  // Expose zoom handlers via ref
  React.useImperativeHandle(ref, () => ({
    handleZoomIn: toggleZoomMode,
    handleResetZoom: resetZoom,
    handleZoomOut: resetZoom
  }), [toggleZoomMode, resetZoom]);

  // OPTIMIZATION: Stable callback for chart hover using useCallback
  const handleChartHover = useCallback((
    xValue: number | null,
    yValue: number | null,
    chartId: string,
    mouseX?: number,
    mouseY?: number
  ) => {
    // OPTIMIZATION 1: State Batching - Update all hover states in single setState call
    if (xValue === null || yValue === null) {
      setHoverState({
        syncedXValue: null,
        syncedYValue: null,
        isHovering: false,
        cursorPosition: null,
      });
    } else {
      setHoverState({
        syncedXValue: xValue,
        syncedYValue: yValue,
        isHovering: true,
        cursorPosition: mouseX !== undefined && mouseY !== undefined ? { x: mouseX, y: mouseY } : null,
      });
    }

    // BIDIRECTIONAL SYNC: Notify external hover handler (e.g., for map sync)
    if (onExternalHover) {
      onExternalHover(xValue, yValue, mouseX, mouseY);
    }
  }, [onExternalHover]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    if (!isFullscreen) {
      containerRef.current?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const currentChartHeight = isFullscreen ? fullscreenChartHeight : chartHeight;

  // Calculate all metric values at the synced position (now includes secondary metrics)
  const allMetricValues = useMemo(() => {
    const activeSyncedXValue = externalSyncedXValue ?? hoverState.syncedXValue;
    if (activeSyncedXValue === null) return null;

    const result: any[] = [];

    groupedMetricConfigs.forEach(({ primary: config, secondary: secondaryConfig }, index) => {
      const chartData = chartDataSets[index];
      if (!chartData || chartData.length === 0) {
        result.push({
          config,
          value: null,
          formattedTime: null,
          formattedDistance: null,
          targetMin: null,
          targetMax: null,
          isMissing: true,
        });
        if (secondaryConfig) {
          result.push({
            config: secondaryConfig,
            value: null,
            formattedTime: null,
            formattedDistance: null,
            targetMin: null,
            targetMax: null,
            isMissing: true,
            isSecondary: true,
          });
        }
        return;
      }

      const xKey = currentXAxisType === 'time' ? 'time' : 'distance';
      const closest = findClosestPointBinarySearch(chartData, activeSyncedXValue, xKey);

      if (!closest) {
        result.push({
          config,
          value: null,
          formattedTime: null,
          formattedDistance: null,
          targetMin: null,
          targetMax: null,
          isMissing: true,
        });
        return;
      }

      // Extract target min/max based on metric type
      let targetMin = null;
      let targetMax = null;
      if (config.id === 'heartRate') {
        targetMin = (closest as any).targetHrMin;
        targetMax = (closest as any).targetHrMax;
      } else if (config.id === 'pace') {
        targetMin = (closest as any).targetPaceMin;
        targetMax = (closest as any).targetPaceMax;
      } else if (config.id === 'power') {
        targetMin = (closest as any).targetPowerMin;
        targetMax = (closest as any).targetPowerMax;
      } else if (config.id === 'speed') {
        targetMin = (closest as any).targetSpeedMin;
        targetMax = (closest as any).targetSpeedMax;
      }

      // Add primary metric
      result.push({
        config,
        value: closest.value,
        formattedTime: closest.formattedTime,
        formattedDistance: closest.formattedDistance,
        targetMin,
        targetMax,
        isMissing: false,
      });

      // Add secondary metric if present
      if (secondaryConfig && closest.secondaryValue !== null && closest.secondaryValue !== undefined) {
        result.push({
          config: secondaryConfig,
          value: closest.secondaryValue,
          formattedTime: closest.formattedTime,
          formattedDistance: closest.formattedDistance,
          targetMin: null,
          targetMax: null,
          isMissing: false,
          isSecondary: true,
        });
      }
    });

    return result.sort((a: any, b: any) => {
      if (a.isMissing === b.isMissing) return 0;
      return a.isMissing ? -1 : 1;
    });
  }, [externalSyncedXValue, hoverState.syncedXValue, groupedMetricConfigs, chartDataSets, currentXAxisType]);

  // OPTIMIZATION: Memoize toggle handlers
  const handleCollapseToggle = useCallback(() => {
    setIsCollapsed(prev => !prev);
  }, []);

  const handleXAxisTypeChange = useCallback((_: any, newValue: 'time' | 'distance' | null) => {
    if (newValue) setCurrentXAxisType(newValue);
  }, []);

  const handleOpenImmersiveAnalysis = useCallback(() => {
    setIsImmersiveAnalysisOpen(true);
  }, []);

  const handleCloseImmersiveAnalysis = useCallback(() => {
    setIsImmersiveAnalysisOpen(false);
  }, []);

  const handleZoomButtonClick = useCallback(() => {
    if (isZoomed) {
      resetZoom();
    } else {
      toggleZoomMode();
    }
  }, [isZoomed, resetZoom, toggleZoomMode]);

  // Memoize tooltip position calculation
  const tooltipPosition = useMemo(() => {
    if (!hoverState.cursorPosition) return null;

    const tooltipWidth = 300;
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
    const isNearRightEdge = hoverState.cursorPosition.x + tooltipWidth + 20 > viewportWidth;
    const leftPosition = isNearRightEdge
      ? hoverState.cursorPosition.x - tooltipWidth - 15
      : hoverState.cursorPosition.x + 15;

    return {
      left: Math.max(10, leftPosition),
      top: Math.max(10, hoverState.cursorPosition.y - 120),
    };
  }, [hoverState.cursorPosition]);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        ...(isFullscreen && {
          position: 'fixed',
          inset: 0,
          zIndex: theme.zIndex.modal,
          bgcolor: 'background.paper',
          p: 3,
          overflow: 'auto',
        }),
      }}
    >
      {/* Header with Title and Controls - Hidden if hideControls is true */}
      {!hideControls && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Performance Charts
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {availableMetrics.some(m => !m.available)
                ? `Missing: ${availableMetrics.filter(m => !m.available).map(m => m.label).join(', ')}`
                : 'Visualize your workout performance and targets'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Box
              onClick={handleCollapseToggle}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                '&:hover': {
                  color: 'text.primary',
                  bgcolor: alpha(theme.palette.action.hover, 0.05),
                },
              }}
            >
              <Typography variant="caption">
                {isCollapsed ? 'Expand' : 'Collapse'}
              </Typography>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  transform: isCollapsed ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms',
                }}
              >
                <path
                  d="M7 14L12 9L17 14"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>

            <IconButton
              onClick={handleZoomButtonClick}
              size="small"
              title={isZoomed ? 'Exit zoom' : isZoomModeActive ? 'Cancel zoom selection' : 'Activate zoom mode'}
              sx={{
                border: '1px solid',
                borderColor: isZoomModeActive || isZoomed ? 'primary.main' : 'divider',
                bgcolor: isZoomModeActive || isZoomed ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
                mr: 1,
              }}
            >
              {isZoomed ? <ZoomOut fontSize="small" /> : <ZoomIn fontSize="small" />}
            </IconButton>

            {showImmersiveButton && (
              <IconButton
                onClick={handleOpenImmersiveAnalysis}
                size="small"
                title="Immersive Analysis - Explore map and metrics together"
                disabled={!streams}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  mr: 1,
                }}
              >
                <Explore fontSize="small" />
              </IconButton>
            )}

            <IconButton
              onClick={toggleFullscreen}
              size="small"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              {isFullscreen ? <FullscreenExitOutlined fontSize="small" /> : <FullscreenOutlined fontSize="small" />}
            </IconButton>
          </Box>
        </Box>
      )}

      {!hideControls && <Divider sx={{ mb: 3 }} />}

      <Collapse in={!isCollapsed || hideControls} timeout={300}>
        {/* Metric Selector - Hidden if hideControls is true */}
        {!hideControls && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary" fontWeight={600}>
                Select Metrics
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={currentXAxisType}
                  exclusive
                  onChange={handleXAxisTypeChange}
                  size="small"
                  sx={{ height: 28, mr: 1 }}
                >
                  <ToggleButton value="time" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                    Time
                  </ToggleButton>
                  <ToggleButton value="distance" sx={{ px: 1.5, py: 0.5, fontSize: '0.75rem' }}>
                    Distance
                  </ToggleButton>
                </ToggleButtonGroup>
                <Button size="small" onClick={selectAll} sx={{ textTransform: 'none' }}>
                  Select All
                </Button>
                <Button size="small" onClick={resetToDefault} sx={{ textTransform: 'none' }}>
                  Reset
                </Button>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
              {availableMetrics.filter(m => m.available).map((config: ChartMetricConfig) => (
                <MetricSelectorChip
                  key={config.id}
                  config={config}
                  selected={selectedMetrics.includes(config.id)}
                  onToggle={(id) => toggleMetric(id as MetricType)}
                  disabled={!canDeselectMetric(config.id) || config.disabled}
                  isSecondary={isSecondaryMetric(config.id)}
                />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary">
              {selectedMetricConfigs.length} metric{selectedMetricConfigs.length !== 1 ? 's' : ''} selected
              {groupedMetricConfigs.some(g => g.secondary) && (
                <span style={{ marginLeft: 8 }}>
                  • Paired metrics shown together for better comparison
                </span>
              )}
            </Typography>
          </Box>
        )}

        {/* OPTIMIZATION: Cursor-following tooltip */}
        {allMetricValues && allMetricValues.length > 0 && hoverState.isHovering && tooltipPosition && createPortal(
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              left: tooltipPosition.left,
              top: tooltipPosition.top,
              zIndex: 10000,
              p: 1.5,
              minWidth: 280,
              maxWidth: 400,
              bgcolor: alpha(theme.palette.background.paper, 0.98),
              border: '2px solid',
              borderColor: theme.palette.primary.main,
              borderRadius: 1.5,
              backdropFilter: 'blur(12px)',
              boxShadow: `0 8px 32px ${alpha(theme.palette.common.black, 0.3)}`,
              pointerEvents: 'none',
            }}
          >
            {/* Time/Distance header */}
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              fontWeight={700}
              textTransform="uppercase"
              letterSpacing={0.5}
              sx={{ mb: 1, borderBottom: '1px solid', borderColor: 'divider', pb: 0.75, fontSize: '0.7rem' }}
            >
              {currentXAxisType === 'time'
                ? allMetricValues[0]?.formattedTime
                : allMetricValues[0]?.formattedDistance}
            </Typography>

            {/* All metric values in compact grid */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              {allMetricValues.map((item: any, idx: number) => (
                <Box key={`${item.config.id}-${idx}`}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1.5,
                    }}
                  >
                    {/* Label with color indicator */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1 }}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          bgcolor: item.config.color,
                          flexShrink: 0,
                          boxShadow: `0 0 4px ${alpha(item.config.color, 0.6)}`,
                          border: item.isSecondary ? `1px dashed ${item.config.color}` : 'none',
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                          fontStyle: item.isSecondary ? 'italic' : 'normal',
                        }}
                      >
                        {item.config.label}
                      </Typography>
                    </Box>
                    {/* Value and unit */}
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: item.value !== null ? item.config.color : 'text.disabled',
                          fontWeight: 700,
                          fontSize: '0.875rem',
                        }}
                      >
                        {item.value !== null
                          ? (item.config.formatValue
                            ? item.config.formatValue(item.value)
                            : `${item.value.toFixed(1)}`)
                          : '--'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontSize: '0.65rem' }}
                      >
                        {item.config.unit}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Show target zone info if available (primary metrics only) */}
                  {!item.isSecondary && item.targetMin !== null && item.targetMin !== undefined &&
                    item.targetMax !== null && item.targetMax !== undefined && (
                      <Box sx={{ ml: 2.5, mt: 0.25 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: '0.65rem',
                            color: theme.palette.mode === 'dark' ? '#999999' : '#6b7280',
                            fontWeight: 600
                          }}
                        >
                          Target: {item.config.formatValue
                            ? `${item.config.formatValue(Math.min(item.targetMin, item.targetMax))} - ${item.config.formatValue(Math.max(item.targetMin, item.targetMax))}`
                            : `${Math.min(item.targetMin, item.targetMax).toFixed(1)} - ${Math.max(item.targetMin, item.targetMax).toFixed(1)}`}
                        </Typography>
                      </Box>
                    )}
                </Box>
              ))}
            </Box>
          </Paper>,
          document.body
        )}

        {/* Grouped Stacked Charts - OPTIMIZED: Fewer chart components */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(1, 1fr)',
              ...(isFullscreen && groupedMetricConfigs.length > 1 && { md: 'repeat(2, 1fr)' }),
            },
            gap: 2,
            overflow: 'visible',
          }}
        >
          {groupedMetricConfigs.map(({ primary: primaryConfig, secondary: secondaryConfig }, index) => {
            const chartData = chartDataSets[index];

            return (
              <Box
                key={`${primaryConfig.id}-${secondaryConfig?.id || 'solo'}`}
                sx={{
                  opacity: 1,
                  transform: 'translateY(0)',
                  transition: 'all 300ms ease-in-out',
                  animation: 'fadeIn 300ms ease-in-out',
                  overflow: 'visible',
                  position: 'relative',
                  '@keyframes fadeIn': {
                    from: {
                      opacity: 0.2,
                      transform: 'translateY(-10px)',
                    },
                    to: {
                      opacity: 1,
                      transform: 'translateY(0)',
                    },
                  },
                }}
              >
                <GroupedMetricChart
                  data={chartData}
                  primaryConfig={primaryConfig}
                  secondaryConfig={secondaryConfig}
                  syncedXValue={externalSyncedXValue ?? hoverState.syncedXValue}
                  onHover={(x, y, mouseX, mouseY) => handleChartHover(x, y, primaryConfig.id, mouseX, mouseY)}
                  xAxisType={currentXAxisType}
                  height={currentChartHeight}
                  sharedXDomain={effectiveXDomain}
                  isLoading={isLoading}
                  onClick={handleChartClick}
                  onMouseMove={handleZoomMouseMove}
                  isZoomModeActive={isZoomModeActive}
                  sliderLeft={sliderLeft}
                  sliderRight={sliderRight}
                  averageValue={getMetricAverage(primaryConfig.id)}
                  secondaryAverageValue={secondaryConfig ? getMetricAverage(secondaryConfig.id) : null}
                  lactateThresholdHr={lactateThresholdHr}
                />
              </Box>
            );
          })}
        </Box>

        {/* Empty state */}
        {selectedMetrics.length === 0 && (
          <Box
            sx={{
              py: 4,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              No metrics selected. Choose metrics above to visualize your performance.
            </Typography>
          </Box>
        )}
      </Collapse>

      {/* Immersive Analysis Dialog */}
      <Suspense fallback={null}>
        <ImmersiveAnalysisDialog
          open={isImmersiveAnalysisOpen}
          onClose={handleCloseImmersiveAnalysis}
          activityName={activityName}
          streams={streams}
          availableMetrics={availableMetrics}
          workoutSegments={workoutSegments}
          polyline={polyline}
          sportType={sportType}
        />
      </Suspense>
    </Box>
  );
});

export default WorkoutAwareChart;
